/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
  SelectionChangedEvent,
} from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useMemo, useRef, useState } from "react"

import { Badge, Button, Commandbar } from "~/components/design-system/components"
import { COMPANY_FLAG_COLUMNS } from "~/features/feature-flags/client/companyFlagColumns"
import {
  useFlagPanel,
  useRequestFlagChange,
} from "~/features/feature-flags/client/FlagPanelProvider"
import { FilterSelect, SearchInput } from "~/features/feature-flags/client/FlagControls"
import { FlagPageWorkspace } from "~/features/feature-flags/client/flag-page/FlagPageWorkspace"
import { useFlagPageKey } from "~/features/feature-flags/client/flag-page/useFlagPageKey"
import { RouteLink } from "~/features/feature-flags/client/primitives"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { Tooltip } from "~/components/reusable/Tooltip"
import { verb } from "~/features/feature-flags/client/model/changeRequest"
import { NO_MATCHES } from "~/features/feature-flags/client/model/filters"
import {
  COMPANY_FILTER_LABELS,
  COMPANY_FILTERS,
  filterCompanyRows,
  flagCompanyRows,
  type CompanyFilter,
  type FlagCompanyRow,
} from "~/features/feature-flags/client/model/flagPage"

/** Checkboxes only: a click in a row is for its controls, not for selecting it. */
const COMPANY_SELECTION: RowSelectionOptions = { mode: "multiRow", enableClickSelection: false }

const FILTER_OPTIONS = COMPANY_FILTERS.map((filter) => ({
  value: filter,
  label: COMPANY_FILTER_LABELS[filter],
}))

const BULK_VALUES = [null, true, false] as const

function CompanyCell({ data }: ICellRendererParams<FlagCompanyRow>) {
  if (!data) return null
  return (
    <span className="flex min-w-0 items-center gap-2">
      <RouteLink
        to="/internal/customer-management/organizations"
        search={{ org: data.id, tab: "feature-flags" }}
        variant="primary"
        size="base"
        className="truncate"
      >
        {data.name}
      </RouteLink>
      {data.builtFor && (
        <Tooltip title="This custom flag was built for this company.">
          <Badge variant="neutral" size="xs" className="shrink-0">
            Built for
          </Badge>
        </Tooltip>
      )}
    </span>
  )
}

const COLUMN_DEFS: ColDef<FlagCompanyRow>[] = [
  {
    colId: "name",
    headerName: "Company",
    valueGetter: ({ data }) => data?.name,
    cellRenderer: CompanyCell,
    cellClass: "ag-cell-vcenter",
    minWidth: 220,
    flex: 2,
  },
  ...COMPANY_FLAG_COLUMNS,
]

/** Every company's status, setting and source for the flag. */
export function FlagCompaniesTab() {
  const flagKey = useFlagPageKey()
  const panel = useFlagPanel()
  const requestChange = useRequestFlagChange()
  const gridRef = useRef<AgGridReact<FlagCompanyRow>>(null)
  const [filter, setFilter] = useState<CompanyFilter>("all")
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<string[]>([])

  const allRows = useMemo(() => flagCompanyRows(panel, flagKey), [panel, flagKey])
  const rows = useMemo(() => filterCompanyRows(allRows, filter, search), [allRows, filter, search])

  const lockReason = panel.lockReason(flagKey, "org")
  const bulk = (value: boolean | null) => {
    requestChange({ kind: "targets", scope: "org", flagKey, targetIds: selected, value })
    gridRef.current?.api.deselectAll()
  }

  return (
    <FlagPageWorkspace
      toolbar={
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <FilterSelect
            label="Show companies"
            width={160}
            options={FILTER_OPTIONS}
            value={filter}
            onChange={setFilter}
          />
          <SearchInput value={search} onChange={setSearch} placeholder="Search companies" />
        </div>
      }
    >
      <DataTableBaseV2<FlagCompanyRow>
        id={`feature-flag-companies-${flagKey}`}
        gridRef={gridRef}
        rowData={rows}
        rowIdField="id"
        columnDefs={COLUMN_DEFS}
        rowSelection={COMPANY_SELECTION}
        onSelectionChanged={(event: SelectionChangedEvent<FlagCompanyRow>) =>
          setSelected(event.api.getSelectedRows().map((row) => row.id))
        }
        loading={panel.loadingCompanies}
        showPagination={false}
        compact
        disableColumnSizingPersistence
        emptyMessage={NO_MATCHES}
      />
      <Commandbar
        count={selected.length}
        selectedText={selected.length === 1 ? "company selected" : "companies selected"}
        onClear={() => gridRef.current?.api.deselectAll()}
      >
        {BULK_VALUES.map((value) => (
          <Button
            key={String(value)}
            size="xs"
            variant="neutral"
            requires="staff"
            allowedTitle={lockReason ?? undefined}
            disabled={!!lockReason}
            onClick={() => bulk(value)}
          >
            {verb(value)}
          </Button>
        ))}
      </Commandbar>
    </FlagPageWorkspace>
  )
}
